import React, { ReactElement } from 'react';
import clsx from 'clsx';
import {
  CircularProgress,
  LabelDisplayedRowsArgs,
  Table,
  TableBody,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
} from '@material-ui/core';
import TableCell from '../TableCell';
import moment from 'moment';
import { createUseStyles } from '../../theming';
import { Order } from '../../interfaces/Table';
import { Link } from 'react-router-dom';

const useStyles = createUseStyles({
  root: {},
  tableContainer: {
    overflowX: 'auto',
    width: '100%',
    paddingLeft: 5,
  },
  iconButton: {
    cursor: 'pointer',
  },
  error: {
    color: '#f44336 !important',
  },
  footer: {
    background: '#fcfbfc',
    borderRadius: 3,
    marginTop: 10,
    padding: 15,
  },
});

type TableColumnType = 'date' | 'datetime' | 'text' | 'bool';

interface BaseTableColumn<Entity> {
  type: TableColumnType;
  numeric?: boolean;
  label: string;
  link?: string | ((entity: Entity) => string);
}

interface IdentityTableColumn<Entity> extends BaseTableColumn<Entity> {
  id: keyof Entity;
  renderValue?: (entity: Entity) => any;
  sortable?: boolean;
}

interface CustomTableColumn<Entity> extends BaseTableColumn<Entity> {
  id: keyof Entity | string | number;
  renderValue: (entity: Entity) => any;
  sortable?: false;
}

export type TableColumn<Entity> = IdentityTableColumn<Entity> | CustomTableColumn<Entity>;

interface Props<Entity> {
  className?: string;
  onChangeLimit: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onChangePage: (event: unknown, newPage: number) => void;
  onSort: (orderBy: keyof Entity) => void;
  actions?: (entity: Entity) => ReactElement;
  entities: Entity[];
  columns: TableColumn<Entity>[];
  hidden?: boolean;
  loading?: boolean;
  error?: string;
  total: number;
  limit: number;
  page: number;
  orderBy?: keyof Entity;
  order?: Order;
  labelEmptyList?: string;
  labelRowsPerPage?: string;
  labelDisplayedRows?: (paginationInfo: LabelDisplayedRowsArgs) => React.ReactNode;
}

const EntityTable = <Entity extends { id: string | number }>(props: Props<Entity>) => {
  const {
    className,
    entities,
    columns,
    onChangePage,
    onChangeLimit,
    onSort,
    actions,
    loading,
    error,
    order,
    orderBy,
    total,
    limit,
    page,
    hidden,
    labelEmptyList,
    labelRowsPerPage,
    labelDisplayedRows,
  } = props;

  const classes = useStyles();

  const renderValue = (entity: Entity, column: IdentityTableColumn<Entity>) => {
    const entityElement = entity[column.id];
    switch (column.type) {
      case 'date':
        return moment(entityElement).format('DD-MM-YY');
      case 'datetime':
        return moment(entityElement).format('DD-MM-YY HH:mm');
      default:
        return entityElement;
    }
  };

  const handleSortEntities = (entity1: Entity, entity2: Entity): number => {
    if (orderBy) {
      const entity2Field = entity2[orderBy];
      const entity1Field = entity1[orderBy];

      switch (typeof entity1Field) {
        case 'number':
          const number2 = (entity2Field as unknown) as number;
          return order === 'desc' ? number2 - entity1Field : entity1Field - number2;
        case 'string':
          const string2 = (entity2Field as unknown) as string;

          if (moment(string2).isValid()) {
            return order === 'desc'
              ? new Date(string2).valueOf() - new Date(entity1Field).valueOf()
              : new Date(entity1Field).valueOf() - new Date(string2).valueOf();
          }

          return order === 'desc' ? string2.localeCompare(entity1Field) : entity1Field.localeCompare(string2);
        default:
          return 0;
      }
    }

    return 0;
  };

  const renderCell = (column: TableColumn<Entity>, entity: Entity) => {
    if (column.renderValue) {
      return column.renderValue(entity);
    }

    return renderValue(entity, column as IdentityTableColumn<Entity>);
  };

  const getLink = (column: TableColumn<Entity>, entity: Entity) => {
    if (column.link) {
      if (typeof column.link === 'string') {
        return column.link;
      } else if (typeof column.link === 'function') {
        return column.link(entity);
      }
    }

    return '';
  };

  return (
    <div hidden={hidden} className={clsx(className, classes.root)}>
      <div className={classes.tableContainer}>
        <Table style={{ minWidth: 815 }}>
          <TableHead>
            <TableRow>
              {columns.map((headCell, key) => (
                <TableCell
                  key={key}
                  align={headCell.numeric ? 'right' : 'left'}
                  sortDirection={orderBy === headCell.id ? order : false}
                >
                  {headCell.sortable ? (
                    <TableSortLabel
                      active={orderBy === headCell.id}
                      direction={orderBy === headCell.id ? order : 'asc'}
                      onClick={() => onSort(headCell.id)}
                    >
                      {headCell.label}
                    </TableSortLabel>
                  ) : (
                    headCell.label
                  )}
                </TableCell>
              ))}

              {!!actions && <TableCell />}
            </TableRow>
          </TableHead>

          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={columns.length + 1} align='center'>
                  <CircularProgress size={15} />
                </TableCell>
              </TableRow>
            )}

            {!error && !loading && entities.length <= 0 && (
              <TableRow>
                <TableCell colSpan={columns.length + 1}>{labelEmptyList || 'Aucun élément trouvé'}</TableCell>
              </TableRow>
            )}

            {error && (
              <TableRow>
                <TableCell colSpan={columns.length + 1} className={classes.error}>
                  {error}
                </TableCell>
              </TableRow>
            )}

            {entities.sort(handleSortEntities).map((entity) => (
              <TableRow key={entity.id}>
                {columns.map((column, key) => (
                  <TableCell key={`${column.id}-${key}`}>
                    {column.link ? (
                      <Link to={getLink(column, entity)}>{renderCell(column, entity)}</Link>
                    ) : (
                      renderCell(column, entity)
                    )}
                  </TableCell>
                ))}

                {!!actions && <TableCell>{actions(entity)}</TableCell>}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <TablePagination
        className={classes.footer}
        rowsPerPageOptions={[5, 10, 25, 50, 100]}
        labelDisplayedRows={
          labelDisplayedRows ? labelDisplayedRows : ({ from, to, count }) => `${from}-${to} sur ${count}`
        }
        labelRowsPerPage={labelRowsPerPage || 'Element par page: '}
        component='div'
        count={total}
        rowsPerPage={limit}
        page={page}
        onPageChange={onChangePage}
        onRowsPerPageChange={onChangeLimit}
        SelectProps={{ native: true }}
      />
    </div>
  );
};

export default EntityTable;
